import { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router';

import {
  Tabs,
  Tab,
  WidgetSettingsBuilder,
  Breadcrumbs,
  Loading,
} from '../../components';
import { getDefaultTab } from './tabs';
import TelephonyTab from './TelephonyTab';
import { useAgent } from './hooks/useAgent';
import WebLinkGeneratorTab from './WebLinkGeneratorTab';
import { useAgentWidgetSettings } from './hooks/useWidgetSettings';
import type { TabsEnum } from './tabs';
import type { FormJsonData } from '../../api';
import type { AgentForm } from '../../utilities';
import { AgentTabs } from './AgentFormTab';

const AgentPageTabs = () => {
  const { params } = useRouteMatch<{
    agent_id: string;
    organization_id: string;
  }>();
  const {
    loading,
    agent,
    form,
    onSubmit,
    onChange,
    knowledgeData,
    activeTab: agentActiveTab,
    setActiveAgentTab,
    ...rest
  } = useAgent(params?.agent_id);
  const {
    handleUpdateWidgetSettings,
    data,
    getWidgetSettings,
    setWidgetData,
    isLoading = true,
    schema,
    handleUploadMedia,
  } = useAgentWidgetSettings();

  const [activeTab, setActiveTab] = useState<TabsEnum>(getDefaultTab);

  const handleSaveWidgetSettings = () => {
    handleUpdateWidgetSettings(params?.agent_id, {
      widgetSettings: { ...data },
    });
  };

  const handleUploadAgentMedia = async (
    file: Blob
  ): Promise<string | undefined> => {
    const response = await handleUploadMedia(params?.agent_id, file);

    return response?.cdnUrl;
  };

  const handleWidgetSettingsChange = (settings: Record<string, unknown>) => {
    const data = {
      ...form,
      widget_settings: settings ? JSON.stringify(settings) : '',
    } as AgentForm;
    setWidgetData(settings);
    onChange(data);
  };

  useEffect(() => {
    if (activeTab === 'widgetFormSettings') {
      getWidgetSettings(params?.agent_id);
    }
  }, [activeTab, getWidgetSettings, params?.agent_id]);

  return (
    <div>
      <div className="flex gap-md flex-wrap justify-between items-center mb-md">
        <Breadcrumbs
          className="text-small"
          subtitle="Agents"
          subtitleUrl={`/agents/${params?.organization_id}`}
          title={agent?.title ?? 'Title'}
        />
      </div>
      <Tabs<TabsEnum>
        tabs={[
          { name: 'agent', label: 'Agent' },
          { name: 'widgetFormSettings', label: 'Widget Settings' },
          { name: 'webLinkGenerator', label: 'Web Link Generator' },
          { name: 'telephony', label: 'Inbound SIP Trunk' },
        ]}
        contentClassName="mt-md"
        activeTab={activeTab}
        onChange={setActiveTab}
      >
        <Tab name="agent">
          <>
            {loading ? (
              <Loading />
            ) : (
              <AgentTabs
                {...rest}
                activeTab={agentActiveTab}
                knowledgeData={knowledgeData || []}
                onSubmit={onSubmit}
                onChange={onChange}
                setActiveTab={setActiveAgentTab}
                agent={agent}
                form={form!}
                loading={loading}
              />
            )}
          </>
        </Tab>
        <Tab name="widgetFormSettings">
          {isLoading ? (
            <Loading />
          ) : (
            <WidgetSettingsBuilder
              onChange={handleWidgetSettingsChange}
              widgetSettings={data}
              schemaData={schema as FormJsonData}
              intializing={isLoading}
              onSave={handleSaveWidgetSettings}
              showSaveButton
              handleFileUpload={handleUploadAgentMedia}
            />
          )}
        </Tab>
        <Tab name="webLinkGenerator">
          <WebLinkGeneratorTab agentId={params?.agent_id} />
        </Tab>
        <Tab name="telephony">
          <TelephonyTab agentId={params?.agent_id} />
        </Tab>
      </Tabs>
    </div>
  );
};

export default AgentPageTabs;
